import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * COMPONENTE PANTALLA GIGANTE: VAS O NO VAS (Maletines)
 * @param {Object} currentGameState - El estado actual del juego inyectado por WebSocket.
 * * Estructura esperada de currentGameState:
 * {
 * phase: 'WAITING' | 'IDLE' | 'SUSPENSE' | 'REVEAL_MODAL',
 * briefcases: [ { id: 1, state: 'closed' | 'selected' | 'opened', prizeTheme: 'none' | 'winner' | 'prize' | 'loser' }, ... ],
 * suspenseBriefcaseId: 3, // El ID del maletín que está temblando en pantalla gigante
 * revealData: { text: "¡GANASTE UN VIAJE!", theme: "winner" } // Los datos para el modal
 * }
 */
const BriefcaseScreen = ({ currentGameState }) => {

    // ==========================================================================
    // EXTRACCIÓN DE DATOS DEL BACKEND
    // ==========================================================================
    const {
        phase = 'WAITING',
        briefcases = [],
        suspenseBriefcaseId = null,
        revealData = null
    } = currentGameState || {};

    // Referencia al lienzo de confeti para dibujar
    const confettiCanvasRef = useRef(null);
    const animationFrameIdRef = useRef(null);
    const confettiParticlesRef = useRef([]);

    // ==========================================================================
    // RUTAS DE ASSETS (Imágenes)
    // ==========================================================================
    const ASSETS = {
        closed: '/img/games/briefcase/cerrado.png',
        opened: '/img/games/briefcase/abierto.png',
        gift: '/img/games/briefcase/regalo.png',
        winner: '/img/games/briefcase/regalo.png',
        //winner: '/img/games/briefcase/premio.png',
        plane1: '/img/games/briefcase/tunita-plane.png',
        plane2: '/img/games/briefcase/lucho-plane.png'
    };

    // ==========================================================================
    // CONFIGURACIÓN DE LA CUADRÍCULA (GRID)
    // Calcula cuántas columnas poner dependiendo de la cantidad de maletines
    // ==========================================================================
    const gridStyle = useMemo(() => {
        const count = briefcases.length || 9; // Por defecto 9
        let columns = 3;
        
        if (count <= 4) columns = 2;
        else if (count > 9 && count <= 16) columns = 4;
        else if (count > 16) columns = 5;

        const rows = Math.ceil(count / columns);

        return {
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`
        };
    }, [briefcases.length]);

    // ==========================================================================
    // LÓGICA DEL CONFETI (Efectos de Partículas)
    // Se activa únicamente cuando se abre el modal y el theme es 'winner'
    // ==========================================================================
    useEffect(() => {
        const canvas = confettiCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        // Ajustar tamaño del canvas a la pantalla
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resizeCanvas);
        resizeCanvas();

        // Creador de partículas
        const createConfettiPiece = (x, y, isBurst = false) => {
            const colors = ['#ffd700', '#ffb300', '#ffffff', '#4ade80', '#60a5fa', '#ff4d4d'];
            return {
                x: x, 
                y: y, 
                r: Math.random() * 6 + 4, 
                color: colors[Math.floor(Math.random() * colors.length)],
                tilt: Math.floor(Math.random() * 10) - 10,
                tiltAngleIncremental: Math.random() * 0.07 + 0.05,
                tiltAngle: Math.random() * Math.PI,
                vx: isBurst ? (Math.random() - 0.5) * 45 : (Math.random() - 0.5) * 2.5,
                vy: isBurst ? (Math.random() * -40) - 20 : Math.random() * 2 + 1.5,
                isBurst: isBurst
            };
        };

        // Bucle de dibujo
        const drawConfetti = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const isWinningPhase = phase.toUpperCase() === 'REVEAL_MODAL' && revealData?.theme === 'winner';

            confettiParticlesRef.current.forEach((p, index) => {
                p.tiltAngle += p.tiltAngleIncremental; 
                p.x += p.vx; 
                p.y += p.vy;

                if (p.isBurst) { 
                    p.vy += 0.85; 
                    p.vx *= 0.98; 
                } else { 
                    p.vy += 0.02; 
                    if (p.vy > 3.5) p.vy = 3.5; 
                    p.vx *= 0.99; 
                }

                p.tilt = Math.sin(p.tiltAngle) * 15;

                // Reposicionar confeti si cae de la pantalla (Lluvia infinita)
                if (p.y > canvas.height + 20) {
                    if (!p.isBurst && isWinningPhase) {
                        p.y = -Math.random() * 120 - 10; 
                        p.x = Math.random() * canvas.width;
                        p.vy = Math.random() * 2 + 1.5; 
                        p.vx = (Math.random() - 0.5) * 2;
                    } else { 
                        // Si ya no estamos ganando, se elimina para que limpie la pantalla
                        confettiParticlesRef.current.splice(index, 1); 
                    }
                }

                // Dibujar la línea (confeti)
                ctx.beginPath(); 
                ctx.lineWidth = p.r / 2; 
                ctx.strokeStyle = p.color;
                ctx.moveTo(p.x + p.tilt + p.r / 4, p.y); 
                ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 4); 
                ctx.stroke();
            });

            animationFrameIdRef.current = requestAnimationFrame(drawConfetti);
        };

        // Disparadores de los cañones laterales
        const fireSideBursts = () => {
            const burstCount = 120;
            for(let i=0; i<burstCount; i++){ 
                confettiParticlesRef.current.push(createConfettiPiece(0, window.innerHeight, true)); 
            }
            for(let i=0; i<burstCount; i++){ 
                let p = createConfettiPiece(window.innerWidth, window.innerHeight, true); 
                p.vx *= -1; 
                confettiParticlesRef.current.push(p); 
            }
        };

        // Lluvia constante desde arriba
        const startConfettiRain = () => {
            for (let i = 0; i < 150; i++) {
                confettiParticlesRef.current.push(createConfettiPiece(Math.random() * canvas.width, Math.random() * canvas.height - canvas.height, false));
            }
        };

        // Escuchar la fase para disparar las explosiones
        if (phase.toUpperCase() === 'REVEAL_MODAL' && revealData?.theme === 'winner') {
            fireSideBursts();
            setTimeout(startConfettiRain, 500);
            if (!animationFrameIdRef.current) drawConfetti();
        } else {
            // Si salimos de la fase de victoria, la lluvia para (se limpian los arrays progresivamente)
            // No cancelamos la animación de golpe para que el confeti termine de caer de forma natural.
        }

        // Limpieza al desmontar el componente
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
            animationFrameIdRef.current = null;
            confettiParticlesRef.current = [];
        };

    }, [phase, revealData]);


    // ==========================================================================
    // HELPERS VISUALES
    // ==========================================================================
    
    // Determina qué imagen poner en el maletín de la cuadrícula
    const getBriefcaseImage = (briefcase) => {
        if (briefcase.state === 'opened') {
            if (briefcase.prizeTheme === 'winner') return ASSETS.winner;
            if (briefcase.prizeTheme === 'prize') return ASSETS.gift;
            return ASSETS.opened;
        }
        return ASSETS.closed; // Cerrado y Selected usan la misma imagen, CSS les da el brillo
    };

    // Determina qué imagen poner en el Modal de Revelación
    const getModalImage = () => {
        if (revealData?.theme === 'winner') return ASSETS.winner;
        if (revealData?.theme === 'prize') return ASSETS.gift;
        return ASSETS.opened;
    };

    return (
        <div className="briefcase-screen">
            <div className="briefcase-screen__overlay-bg"></div>

            <img src="/img/page/adsl-five.png" className="LogoADSLxZtudia" alt="Logo" style={{ width: '100%', maxWidth: '300px', marginTop: '10px', zIndex: '201', position: 'fixed', top: '0', left: '50%', transform: 'translateX(-50%)' }} />

            {/* CONTENIDO PRINCIPAL: LA CUADRÍCULA */}
            <div className={`briefcase-screen__content ${phase.toUpperCase() === 'SUSPENSE' ? 'briefcase-screen__content--blurred' : ''}`}>
                <div className="briefcase-screen__grid" style={gridStyle}>
                    
                    {/* Renderizado dinámico de la lista inyectada por C# */}
                    {briefcases.map((bc, index) => (
                        <div 
                            key={bc.id} 
                            className={`briefcase-screen__item 
                                ${bc.state === 'closed' ? 'briefcase-screen__item--active' : ''}
                                ${bc.state === 'selected' ? 'briefcase-screen__item--selected' : ''}
                                ${bc.state === 'opened' ? 'briefcase-screen__item--opened' : ''}
                            `}
                            // Valores aleatorios para que cada maletín tiemble de forma asimétrica
                            style={{ 
                                '--jitter-duration': `${(2.5 + (index % 3)).toFixed(2)}s`,
                                '--jitter-delay': `${((index * 0.3) % 2).toFixed(2)}s`
                            }}
                        >
                            <div 
                                className="briefcase-screen__image" 
                                style={{ backgroundImage: `url(${getBriefcaseImage(bc)})` }}
                            >
                                <span className="briefcase-screen__number">{bc.id}</span>
                            </div>
                        </div>
                    ))}

                </div>
            </div>

            {/* OVERLAY DE SUSPENSO: Maletín gigante temblando en medio de la pantalla */}
            {phase.toUpperCase() === 'SUSPENSE' && (
                <div className="briefcase-screen__suspense">
                    <div className="briefcase-screen__suspense-briefcase">
                        <div 
                            className="briefcase-screen__image" 
                            style={{ backgroundImage: `url(${ASSETS.closed})` }}
                        >
                            <span className="briefcase-screen__suspense-number">{suspenseBriefcaseId}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE RESULTADO: El premio es revelado */}
            {phase.toUpperCase() === 'REVEAL_MODAL' && revealData && (
                <div className="briefcase-screen__modal">
                    
                    {/* Aviones Escoltas (Solo aparecen si el tema es 'winner') */}
                    {/* {revealData.theme === 'winner' && (
                        <>
                            <div className="briefcase-screen__plane briefcase-screen__plane--left" style={{ backgroundImage: `url(${ASSETS.plane1})` }}></div>
                            <div className="briefcase-screen__plane briefcase-screen__plane--right" style={{ backgroundImage: `url(${ASSETS.plane2})` }}></div>
                        </>
                    )} */}

                    <div className="briefcase-screen__modal-box-wrapper" style={{ width: '100%', maxWidth: '42rem', position: 'relative' }}>
                        
                        <div className={`briefcase-screen__modal-box briefcase-screen__modal-box--${revealData.theme}`}>
                            <div className="briefcase-screen__modal-image-container" style={{ backgroundImage: `url(${getModalImage()})` }}></div>
                            
                            <div className="briefcase-screen__modal-text">
                                {revealData.text}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* LIENZO PARA EL CONFETI */}
            <canvas ref={confettiCanvasRef} className="briefcase-screen__confetti"></canvas>
            
            <p className='FooterCredits' style={{ position: 'fixed', bottom: '0', left: '50%', transform: 'translateX(-50%)', zIndex: '201' }}>Hecho con ♥ por Five Digital Minds | 2026</p>
            {/* <p className='FooterCredits' style={{ textAlign: 'center', zIndex: '12', color: '#fff', fontSize: '18px' }}>Hecho con ♥ por Five Digital Minds | 2026</p> */}

        </div>
    );
};

export { BriefcaseScreen };